/** Pure Entitlements (دسترسی پلان) display mapping — no I/O. D4I-D. */

import type { StatusTone } from "./overview-ops";

export type EntitlementFeatureKey = "publish" | "bundleGenerate" | "sponsored";
export type EntitlementLimitKey = "activeSites" | "activeMembers" | "storageBytes";
export type EntitlementRowKind = "feature" | "limit";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

export type EntitlementStatusLike = {
  organizationId: string;
  planId: string;
  planCode: string;
  planDisplayName: string;
  notes: string | null;
  features: { publish: boolean; bundleGenerate: boolean; sponsored: boolean };
  usage: {
    activeSites: {
      usage: number;
      max: number | null;
      warning: boolean;
      exceeded: boolean;
    };
    activeMembers: {
      usage: number;
      max: number | null;
      warning: boolean;
      exceeded: boolean;
    };
    storageBytes: {
      usage: number;
      max: number | null;
      warning: boolean;
      exceeded: boolean;
    };
  };
  overrides: Record<string, unknown>;
  assignedAt: string;
  updatedAt: string;
};

export type EntitlementInventoryRow = {
  id: string;
  kind: EntitlementRowKind;
  featureKey?: EntitlementFeatureKey;
  limitKey?: EntitlementLimitKey;
  labelFa: string;
  technicalId: string;
  /** Effective allowed/denied for features; usage band for limits. */
  accessLabel: string;
  accessTone: StatusTone;
  source: "plan_default" | "org_override";
  sourceLabel: string;
  usageText?: string;
  maxText?: string;
  /** Sponsored gets explicit operator-facing emphasis. */
  isSponsored: boolean;
};

export type EntitlementSortKey = "label" | "kind" | "access" | "source";

export const ENTITLEMENTS_PAGE_LABEL = "دسترسی پلان";

export const ENTITLEMENTS_PAGE_NOTE =
  "دسترسی پلان نشان می‌دهد طرح تخصیص‌یافته، امکانات مؤثر، و تفاوت پیش‌فرض طرح با تغییر صریح سازمان. فقط سازمان فعال نمایش داده می‌شود؛ رفتار سقف‌ها و پرچم‌ها تغییر نمی‌کند.";

export const SPONSORED_FEATURE_NOTE =
  "تبلیغات ویژه (sponsored): وضعیت مؤثر از طرح یا تغییر سازمان می‌آید. در این صفحه کنترل جداگانهٔ تغییر آگهی اضافه نشده — فقط نمایش وضعیت موجود.";

const FEATURE_META: Record<
  EntitlementFeatureKey,
  { labelFa: string; overrideKey: string; technicalId: string }
> = {
  publish: {
    labelFa: "انتشار محتوا",
    overrideKey: "allowPublish",
    technicalId: "publish",
  },
  bundleGenerate: {
    labelFa: "تولید Bundle",
    overrideKey: "allowBundleGenerate",
    technicalId: "bundleGenerate",
  },
  sponsored: {
    labelFa: "تبلیغات ویژه",
    overrideKey: "allowSponsored",
    technicalId: "sponsored",
  },
};

const LIMIT_META: Record<
  EntitlementLimitKey,
  { labelFa: string; overrideKey: string; usageKey: keyof EntitlementStatusLike["usage"] }
> = {
  activeSites: {
    labelFa: "سایت‌های فعال",
    overrideKey: "maxActiveSites",
    usageKey: "activeSites",
  },
  activeMembers: {
    labelFa: "اعضای فعال",
    overrideKey: "maxActiveMembers",
    usageKey: "activeMembers",
  },
  storageBytes: {
    labelFa: "فضای فایل",
    overrideKey: "maxStorageBytes",
    usageKey: "storageBytes",
  },
};

export function formatEntitlementLimit(max: number | null): string {
  return max == null ? "نامحدود" : String(max);
}

export function formatEntitlementBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function featureAccessDisplay(allowed: boolean): StatusDisplay {
  return allowed
    ? { label: "مجاز", tone: "success" }
    : { label: "غیرمجاز", tone: "danger", detail: "امکان برای این سازمان فعال نیست" };
}

export function limitStatusDisplay(row: {
  warning: boolean;
  exceeded: boolean;
}): StatusDisplay {
  if (row.exceeded) return { label: "پر", tone: "danger", detail: "مصرف به سقف رسیده" };
  if (row.warning) return { label: "هشدار", tone: "warning", detail: "نزدیک سقف" };
  return { label: "عادی", tone: "success" };
}

export function sourceDisplay(hasOverride: boolean): StatusDisplay {
  return hasOverride
    ? {
        label: "تغییر سازمان",
        tone: "warning",
        detail: "مقدار صریح سازمان؛ نه فقط پیش‌فرض طرح",
      }
    : {
        label: "پیش‌فرض طرح",
        tone: "info",
        detail: "از طرح تخصیص‌یافته به ارث رسیده",
      };
}

function hasExplicitOverride(overrides: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] != null;
}

export function buildEntitlementInventory(
  status: EntitlementStatusLike,
): EntitlementInventoryRow[] {
  const features: EntitlementInventoryRow[] = (
    Object.keys(FEATURE_META) as EntitlementFeatureKey[]
  ).map((key) => {
    const meta = FEATURE_META[key];
    const allowed = status.features[key];
    const access = featureAccessDisplay(allowed);
    const overridden = hasExplicitOverride(status.overrides, meta.overrideKey);
    const source = sourceDisplay(overridden);
    return {
      id: `feature:${key}`,
      kind: "feature",
      featureKey: key,
      labelFa: meta.labelFa,
      technicalId: meta.technicalId,
      accessLabel: access.label,
      accessTone: access.tone,
      source: overridden ? "org_override" : "plan_default",
      sourceLabel: source.label,
      isSponsored: key === "sponsored",
    };
  });

  const limits: EntitlementInventoryRow[] = (
    Object.keys(LIMIT_META) as EntitlementLimitKey[]
  ).map((key) => {
    const meta = LIMIT_META[key];
    const usage = status.usage[meta.usageKey];
    const access = limitStatusDisplay(usage);
    const overridden = hasExplicitOverride(status.overrides, meta.overrideKey);
    const source = sourceDisplay(overridden);
    const usageText =
      key === "storageBytes"
        ? formatEntitlementBytes(usage.usage)
        : String(usage.usage);
    const maxText =
      key === "storageBytes"
        ? usage.max == null
          ? "نامحدود"
          : formatEntitlementBytes(usage.max)
        : formatEntitlementLimit(usage.max);
    return {
      id: `limit:${key}`,
      kind: "limit",
      limitKey: key,
      labelFa: meta.labelFa,
      technicalId: meta.overrideKey,
      accessLabel: access.label,
      accessTone: access.tone,
      source: overridden ? "org_override" : "plan_default",
      sourceLabel: source.label,
      usageText,
      maxText,
      isSponsored: false,
    };
  });

  return [...features, ...limits];
}

export function matchesEntitlementQuery(row: EntitlementInventoryRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.labelFa,
    row.technicalId,
    row.accessLabel,
    row.sourceLabel,
    row.usageText ?? "",
    row.maxText ?? "",
    row.isSponsored ? "تبلیغات ویژه sponsored" : "",
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesEntitlementFilter(row: EntitlementInventoryRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "features") return row.kind === "feature";
  if (filter === "limits") return row.kind === "limit";
  if (filter === "allowed") return row.kind === "feature" && row.accessLabel === "مجاز";
  if (filter === "denied") return row.kind === "feature" && row.accessLabel === "غیرمجاز";
  if (filter === "overridden") return row.source === "org_override";
  if (filter === "inherited") return row.source === "plan_default";
  if (filter === "sponsored") return row.isSponsored;
  return true;
}

export function filterAndSortEntitlements(
  rows: EntitlementInventoryRow[],
  opts: { query: string; filter: string; sort: EntitlementSortKey },
): EntitlementInventoryRow[] {
  const filtered = rows.filter(
    (r) => matchesEntitlementQuery(r, opts.query) && matchesEntitlementFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "kind") {
      const kindCmp = a.kind.localeCompare(b.kind);
      return kindCmp !== 0 ? kindCmp : a.labelFa.localeCompare(b.labelFa, "fa");
    }
    if (opts.sort === "access") {
      const accessCmp = a.accessLabel.localeCompare(b.accessLabel, "fa");
      return accessCmp !== 0 ? accessCmp : a.labelFa.localeCompare(b.labelFa, "fa");
    }
    if (opts.sort === "source") {
      const sourceCmp = a.sourceLabel.localeCompare(b.sourceLabel, "fa");
      return sourceCmp !== 0 ? sourceCmp : a.labelFa.localeCompare(b.labelFa, "fa");
    }
    return a.labelFa.localeCompare(b.labelFa, "fa");
  });
  return sorted;
}

export function buildUsageWarnings(status: EntitlementStatusLike): string[] {
  const items: string[] = [];
  if (status.usage.activeSites.warning) {
    items.push(
      `سایت‌های فعال نزدیک سقف هستند (${status.usage.activeSites.usage}/${formatEntitlementLimit(status.usage.activeSites.max)})`,
    );
  }
  if (status.usage.activeMembers.warning) {
    items.push(
      `اعضای فعال نزدیک سقف هستند (${status.usage.activeMembers.usage}/${formatEntitlementLimit(status.usage.activeMembers.max)})`,
    );
  }
  if (status.usage.storageBytes.warning) {
    items.push(
      `فضای فایل نزدیک سقف است (${formatEntitlementBytes(status.usage.storageBytes.usage)} / ${status.usage.storageBytes.max == null ? "نامحدود" : formatEntitlementBytes(status.usage.storageBytes.max)})`,
    );
  }
  return items;
}

export type RelatedEntitlementsLink = { id: string; label: string; to: string };

export function relatedEntitlementsLinks(): RelatedEntitlementsLink[] {
  return [
    { id: "sponsored", label: "تبلیغات ویژه", to: "/sponsored" },
    { id: "memberships", label: "اعضای سازمان", to: "/memberships" },
    { id: "sites", label: "سایت‌ها", to: "/sites" },
    { id: "overview", label: "نمای کلی", to: "/" },
  ];
}
